import React, { useEffect, useState } from 'react'
import axios from 'axios'
import { useHistory, useParams } from 'react-router-dom'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import { loadStripe } from '@stripe/stripe-js'
import styles from './InvoicePaymentPage.module.css'

const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
}).format(Number(value) || 0)

const apiUrl = (path) => `${process.env.REACT_APP_API}${path}`

const PaymentShell = ({ children }) => (
  <main className={styles.paymentPage}>
    <div className={styles.pageFrame}>
      <header className={styles.brandHeader}>
        <img src="/images/wholesale_shield.PNG" alt="Wholesale USA" className={styles.logo} />
        <div>
          <span className={styles.eyebrow}>Secure invoice payment</span>
          <h1>Wholesale USA</h1>
        </div>
      </header>
      {children}
      <footer className={styles.paymentFooter}>Payments are securely processed by Stripe.</footer>
    </div>
  </main>
)

const LinkNotice = ({ state, message }) => {
  const title = state === 'paid'
    ? 'This invoice is already paid'
    : state === 'processing'
      ? 'Your payment is processing'
      : 'This payment link is unavailable'
  return (
    <section className={styles.noticeCard}>
      <div className={styles.noticeIcon}>{state === 'paid' ? '✓' : '!'}</div>
      <h2>{title}</h2>
      <p>{message || (state === 'expired' ? 'The payment link has expired. Please contact Wholesale USA for a new link.' : 'Please contact Wholesale USA for assistance.')}</p>
    </section>
  )
}

const InvoiceSummary = ({ invoice }) => (
  <section className={styles.summaryCard}>
    <div className={styles.cardHeading}>
      <div>
        <span className={styles.eyebrow}>Invoice summary</span>
        <h2>{invoice.invoiceNumber ? `Invoice #${invoice.invoiceNumber}` : 'Your order'}</h2>
      </div>
      <div className={styles.amountDue}>
        <span>Balance due</span>
        <strong>{formatCurrency(invoice.balanceDue)}</strong>
      </div>
    </div>

    <div className={styles.itemTable}>
      <div className={`${styles.itemRow} ${styles.itemHeader}`}>
        <span>Item</span><span>Qty</span><span>Price</span><span>Amount</span>
      </div>
      {invoice.items.map((item, index) => (
        <div className={styles.itemRow} key={`${item.modelNumber}-${index}`}>
          <span className={styles.itemName}>
            {item.modelNumber}
            {item.discountPercent > 0 && <small>{item.discountPercent}% discount</small>}
          </span>
          <span>{item.quantity}</span>
          <span>{formatCurrency(item.unitPrice)}</span>
          <strong>{formatCurrency(item.lineTotal)}</strong>
        </div>
      ))}
    </div>

    <div className={styles.totals}>
      <div><span>Subtotal</span><span>{formatCurrency(invoice.subTotal)}</span></div>
      {invoice.deliveryFee !== 0 && <div><span>Delivery</span><span>{formatCurrency(invoice.deliveryFee)}</span></div>}
      {invoice.additionalFee !== 0 && <div><span>{invoice.additionalFeeLabel}</span><span>{formatCurrency(invoice.additionalFee)}</span></div>}
      {invoice.salesTax !== 0 && <div><span>Sales tax</span><span>{formatCurrency(invoice.salesTax)}</span></div>}
      <div className={styles.invoiceTotal}><span>Invoice total</span><span>{formatCurrency(invoice.total)}</span></div>
      <div className={styles.balanceTotal}><span>Amount to pay</span><strong>{formatCurrency(invoice.balanceDue)}</strong></div>
    </div>
  </section>
)

const EmbeddedPaymentForm = ({ token, expiresAt }) => {
  const stripe = useStripe()
  const elements = useElements()
  const history = useHistory()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const submitPayment = async (event) => {
    event.preventDefault()
    if (!stripe || !elements) return
    setSubmitting(true)
    setError('')
    const returnUrl = `${window.location.origin}/pay/${encodeURIComponent(token)}/success`
    const result = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: returnUrl },
      redirect: 'if_required',
    })
    if (result.error) {
      setError(result.error.message || 'The payment could not be completed.')
      setSubmitting(false)
      return
    }
    history.replace(`/pay/${token}/success`)
  }

  return (
    <section className={styles.paymentCard}>
      <div className={styles.paymentHeading}>
        <span className={styles.eyebrow}>Payment details</span>
        <h2>Pay by ACH</h2>
        <p>Your secure payment session expires {new Date(expiresAt).toLocaleString()}.</p>
      </div>
      <form onSubmit={submitPayment}>
        <PaymentElement options={{ layout: 'accordion' }} />
        {error && <div className={styles.formError}>{error}</div>}
        <button className={styles.primaryButton} type="submit" disabled={!stripe || submitting}>
          {submitting ? 'Processing payment…' : 'Pay invoice securely'}
        </button>
      </form>
      <p className={styles.securityNote}>Wholesale USA does not store your bank account details.</p>
    </section>
  )
}

const InvoicePaymentPage = () => {
  const { token } = useParams()
  const [link, setLink] = useState(null)
  const [session, setSession] = useState(null)
  const [stripePromise, setStripePromise] = useState(null)
  const [loading, setLoading] = useState(true)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let active = true
    axios.get(apiUrl(`/payments/links/${encodeURIComponent(token)}`))
      .then(({ data }) => { if (active) setLink(data) })
      .catch((requestError) => {
        if (active) setError(requestError?.response?.data?.message || 'Unable to open this payment link.')
      })
      .finally(() => { if (active) setLoading(false) })
    return () => { active = false }
  }, [token])

  const startPayment = async () => {
    setStarting(true)
    setError('')
    try {
      const { data } = await axios.post(apiUrl(`/payments/links/${encodeURIComponent(token)}/session`))
      setSession(data)
      setStripePromise(loadStripe(data.publishableKey))
    } catch (requestError) {
      const responseData = requestError?.response?.data
      const message = responseData?.message || 'Unable to start this payment.'
      const terminalStates = {
        balance_changed: 'revoked',
        invoice_paid: 'paid',
        expired: 'expired',
        revoked: 'revoked',
        payment_processing: 'processing',
        processing: 'processing',
        paid: 'paid',
      }
      if (terminalStates[responseData?.code]) {
        setLink((current) => ({
          ...current,
          state: terminalStates[responseData.code],
          invoice: null,
          failureMessage: message,
        }))
      }
      setError(message)
    } finally {
      setStarting(false)
    }
  }

  if (loading) return <PaymentShell><div className={styles.loadingCard}>Loading your invoice…</div></PaymentShell>
  if (!link) return <PaymentShell><LinkNotice message={error} /></PaymentShell>
  if (link.state !== 'active' || !link.invoice) {
    return <PaymentShell><LinkNotice state={link.state} message={link.failureMessage || error} /></PaymentShell>
  }

  const invoice = session?.invoice || link.invoice
  return (
    <PaymentShell>
      <div className={styles.paymentGrid}>
        <InvoiceSummary invoice={invoice} />
        <div>
          {!session ? (
            <section className={styles.startCard}>
              <span className={styles.eyebrow}>Ready when you are</span>
              <h2>Pay securely online</h2>
              <p>Pay the balance by ACH with instant bank verification. Starting payment opens a {link.sessionLengthMinutes}-minute secure session.</p>
              {link.failureMessage && <div className={styles.formError}>{link.failureMessage}</div>}
              {error && <div className={styles.formError}>{error}</div>}
              <button className={styles.primaryButton} onClick={startPayment} disabled={starting}>
                {starting ? 'Starting secure payment…' : 'Continue to secure payment'}
              </button>
              <p className={styles.expiryNote}>This link initially expires {new Date(link.expiresAt).toLocaleString()}.</p>
            </section>
          ) : stripePromise ? (
            <Elements
              stripe={stripePromise}
              options={{
                clientSecret: session.clientSecret,
                appearance: {
                  theme: 'stripe',
                  variables: { colorPrimary: '#173f35', borderRadius: '8px', fontFamily: 'Arial, sans-serif' },
                },
              }}
            >
              <EmbeddedPaymentForm token={token} expiresAt={session.expiresAt} />
            </Elements>
          ) : null}
        </div>
      </div>
    </PaymentShell>
  )
}

export default InvoicePaymentPage
